import * as React from 'react';

import cx from 'classnames';

import Icon from 'components/IconDeprecated';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import TooltipTrigger from 'components/TooltipTrigger';
import Checkbox from 'components/Checkbox';
import LayerTrigger from 'components/LayerTrigger';

import { ColumnHeader, SortDirection } from 'components/ComposableTable';

import './ComposableTableHeader.scss';

const ICON_WIDTH = 16;
const OS_BROWSER_RENDERING_AFFORDANCE = 4;
const SORT_ICON_WIDTH = 16;
const TABLE_CELL_PADDING_X = 16;
const WIDTH_AFFORDANCE =
  ICON_WIDTH + SORT_ICON_WIDTH + TABLE_CELL_PADDING_X * 2 + OS_BROWSER_RENDERING_AFFORDANCE;

export type ComposableTableHeaderSortProps = {
  onClick: Function;
  sortColumn?: string;
  sortDirection?: SortDirection;
};

type Props = {
  columnHeader: ColumnHeader;
  sortProps?: ComposableTableHeaderSortProps;
  topOffset?: number;
  index: number;
  spreadsheet?: boolean;
  selectionMenu?: React.ReactElement;
};

export default class ComposableTableHeader extends React.Component<Props> {
  getNewSortDirection = () => {
    const { columnHeader, sortProps } = this.props;

    // flow necessitates
    if (!sortProps) {
      return '';
    }

    const { sortValue } = columnHeader;
    const { sortColumn, sortDirection } = sortProps;

    if (sortValue === sortColumn) {
      return sortDirection === 'asc' ? 'desc' : 'asc';
    }

    return 'asc';
  };

  addColumnLetters = (component: React.ReactElement) => {
    // converts 0 = A, 1 = B, 2 = C, etc.
    const indexToAlphaLetter = (index: number) => String.fromCharCode(index + 65);

    return (
      <MediaObject center gutter="small">
        <Text color="graphite">{indexToAlphaLetter(this.props.index)}</Text>
        {component}
      </MediaObject>
    );
  };

  handleClick = () => {
    const { columnHeader, sortProps } = this.props;

    // flow necessitates
    if (!sortProps) {
      return;
    }

    const { sortValue } = columnHeader;
    const { onClick } = sortProps;

    onClick(sortValue, this.getNewSortDirection());
  };

  renderHeaderText = () => {
    const { columnHeader } = this.props;
    const { icon, name } = columnHeader;
    return (
      <MediaObject center figure="right" gutter="xxsmall">
        <Text data-test="column-header">{name}</Text>
        {icon ? (
          <TooltipTrigger tooltip={icon.tooltip} placement="top">
            <span>
              <Icon name={icon.iconName} size="inherit" />
            </span>
          </TooltipTrigger>
        ) : (
          <span />
        )}
      </MediaObject>
    );
  };

  render() {
    const { columnHeader, sortProps, topOffset, spreadsheet } = this.props;
    const { sortValue, type, width, selectionMenu, selectionState } = columnHeader;

    const style: { width?: number; top?: number } = {};
    if (width) {
      style.width = width + WIDTH_AFFORDANCE;
    }
    if (topOffset) {
      style.top = topOffset;
    }

    const checkbox = (
      <MediaObject gutter="xsmall" inline>
        <Checkbox
          indeterminate={selectionState?.someSelected()}
          checked={selectionState?.allSelected()}
          readOnly
        />
        <Icon className="ComposableTableHeader-selectionMenu-arrow" name="chevron-down" />
      </MediaObject>
    );

    const content = (
      <MediaObject center figure="right" gutter="xxsmall" inline>
        {spreadsheet ? this.addColumnLetters(this.renderHeaderText()) : this.renderHeaderText()}
        {sortProps && sortProps.sortColumn === sortValue && (
          <Icon
            name={sortProps.sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
            size="inherit"
          />
        )}
      </MediaObject>
    );

    const header = (
      <th
        key={columnHeader.name}
        style={style}
        tabIndex={sortValue ? 0 : undefined}
        className={cx(
          'ComposableTableHeader',
          (sortValue || selectionMenu) && 'ComposableTableHeader--actionable',
          type && `ComposableTableHeader--${type}`
        )}
        onClick={sortProps ? this.handleClick : undefined}
        data-testid={columnHeader['data-testid']}
      >
        {selectionMenu ? checkbox : content}
      </th>
    );

    if (selectionMenu) {
      return (
        <LayerTrigger layer={selectionMenu} placement="bottom" alignment="left" trigger="click">
          {header}
        </LayerTrigger>
      );
    } else {
      return header;
    }
  }
}
